import React, { useRef, useState, useEffect } from 'react';
import CustomAppBar from '../../../mainComponents/appBar';
import { useUserUpdateMutation } from '../../../redux/setting';
import toast from 'react-hot-toast';
import { STRINGS } from '../../../utils/strings';
import { Camera } from 'lucide-react';
import { FaEdit } from 'react-icons/fa';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setProfilePicture } from '../../../redux/features/userSlice';
import { useGetUserQuery, useLazyGetUserQuery } from '../../../redux/auth';
import { secureStorageUtils } from '../../../utils/secureStorageUtils';


export default function UpdateProfilePicture() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();

  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [userUpdate] = useUserUpdateMutation();
  const { data: userData, isLoading } = useGetUserQuery();
  const [triggerGetUser] = useLazyGetUserQuery();

  // Set existing image if present
  useEffect(() => {
    // Priority: API profile picture > stored profile picture > no image
    const apiProfilePicture = userData?.data?.profile_picture;
    if (apiProfilePicture && !previewUrl) {
      setPreviewUrl(apiProfilePicture);
    }
  }, [userData, previewUrl]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleUpload = async () => {
    if (!selectedImage) return;
    setIsUploading(true);
    try {
      const secure_url = await uploadImageToCloudinary(selectedImage);
      await userUpdate({
        profilePicture: secure_url
      }).unwrap();
      
      // Refetch user details and update secure storage
      try {
        const updatedUserData = await triggerGetUser().unwrap();
        if (updatedUserData?.data) {
          secureStorageUtils.setItem("userData", JSON.stringify(updatedUserData.data));
          localStorage.setItem("hasFetchedUser", "true");
        }
      } catch (refetchError) {
        console.error("Failed to refetch user data:", refetchError);
      }
      
      dispatch(setProfilePicture(secure_url));
      setSelectedImage(null);
      setPreviewUrl(secure_url);
      toast.success('Profile picture updated!');
    } catch (error) {
      toast.error('Failed to update profile picture');
    } finally {
      setIsUploading(false);
    }
  };

  const uploadImageToCloudinary = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', STRINGS.UPLOAD_PRESET || '');
    const res = await fetch(
      `https://api.cloudinary.com/v1_1/${STRINGS.CLOUD_NAME}/image/upload`,
      { method: 'POST', body: formData }
    );
    if (!res.ok) throw new Error('Image upload failed');
    const data = await res.json();
    return data.secure_url;
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="flex justify-center bg-[#f5f5f5] overflow-hidden pt-10 h-screen px-4 py-6 text-black font-[SF Pro Display]">
      <div className="w-full max-w-sm">
        <CustomAppBar title="Update Profile Picture" />

        <div className="bg-white rounded-2xl mt-5 p-6 text-center shadow-md space-y-4">
          <p className="text-sm text-gray-600">Tap to upload a new profile picture</p>

          {/* Profile Picture Circle with Pencil */}
          <div className="relative flex justify-center">
            <div
              onClick={triggerFileSelect}
              className="w-[150px] h-[150px] rounded-full border-4 border-[#6d7680a9] p-1 flex items-center justify-center overflow-hidden cursor-pointer hover:shadow-md transition"
            >
              <div className="bg-[#6d7680a9] w-full h-full flex items-center justify-center rounded-full">
                {isLoading ? (
                  <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : previewUrl ? (
                  <img
                    src={previewUrl}
                    className="w-full h-full object-cover rounded-full" //Rounded here
                    alt="Profile"
                  />
                ) : (
                  <Camera className="w-8 h-8 text-white opacity-60" />
                )}
              </div>
            </div>


            {/* Pencil Icon */}
            <div
              onClick={triggerFileSelect}
              className="absolute bottom-2 right-[calc(50%-75px*-0.5+8px)] bg-white border border-gray-300 rounded-full p-2 shadow hover:scale-105 transition cursor-pointer"
            >
              <FaEdit className="w-4 h-4 text-gray-600" />
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="hidden"
          />

          {/* Upload Button */}
          {selectedImage && (
            <button
              onClick={handleUpload}
              disabled={isUploading}
              className={`w-full py-3 rounded-xl font-semibold text-white shadow transition-all ${isUploading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:brightness-105'
                }`}
            >
              {isUploading ? 'Uploading...' : 'Upload Profile Picture'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
